import { Button, Card, Col, Empty, Flex, Row, Skeleton, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { KpiCard } from "../../../components/widgets/kpi/KpiWidget.tsx"
import type { KpiTile } from "../../../components/widgets/types.ts"
import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { getFileIcon } from "../../Project/ProjectViewPage/helpers/file.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { AssignDocumentTypeModal } from "../../Files/components/AssignDocumentTypeModal.tsx"
import { ActivityDrawer } from "../../Files/components/ActivityDrawer.tsx"
import { CommentsDrawer } from "../../Files/components/CommentsDrawer.tsx"
import { RequestReviewModal } from "../../Files/components/RequestReviewModal.tsx"
import { ShareDialog } from "../../Files/components/ShareDialog.tsx"
import { UploadNewVersionModal } from "../../Files/components/UploadNewVersionModal.tsx"
import { VersionHistoryDrawer } from "../../Files/components/VersionHistoryDrawer.tsx"
import { fileItemKey, mapDocumentToFileItem } from "../../Files/helpers/map-to-file-item.ts"
import { useFavorites } from "../../Files/hooks/useFavorites.ts"
import { FileViewer } from "../../Files/components/FileViewer.tsx"
import { type FileItem } from "../../Files/types/file-item.types.ts"
import type { ProjectDocument, RelatedRecord } from "../../Project/types/project-documents.types.ts"
import { RenameFileModal } from "../../Project/ProjectViewPage/components/RenameFileModal.tsx"
import { GlobalLinkRecordModal } from "./GlobalLinkRecordModal.tsx"
import { useGlobalFilesData } from "../hooks/useGlobalFilesData.ts"
import { useGlobalFileActions } from "../hooks/useGlobalFileActions.ts"
import { useGlobalFilesOverview } from "../hooks/useGlobalFilesOverview.ts"

const { Text } = Typography

interface GlobalFilesOverviewTabProps {
  onNavigateToTab: (
    tab: "all-files" | "business-documents" | "photos",
    extra?: Record<string, string>
  ) => void
}

export function GlobalFilesOverviewTab({ onNavigateToTab }: GlobalFilesOverviewTabProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const overviewState = useGlobalFilesOverview()
  const recent = useGlobalFilesData("all", undefined, "modified_desc")
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null)

  const recentFiles = useMemo(() => recent.items.slice(0, 5), [recent.items])

  const favorites = useFavorites("global")

  const navigateToProject = (file: FileItem) => {
    const info = file.projectInfo
    if (!info) return
    navigate(info.is_project ? RoutesPaths.project.view(info.id).overview : RoutesPaths.lead.view(info.id))
  }

  const handleViewRecord = (record: RelatedRecord) => {
    if (record.type === "proposal") navigate(RoutesPaths.proposal.view(record.id))
    else if (record.type === "contract") navigate(RoutesPaths.contract.view(record.id))
    else if (record.type === "project") navigate(RoutesPaths.project.view(record.id).overview)
    else if (record.type === "lead") navigate(RoutesPaths.lead.view(record.id))
    else if (record.type === "invoice" && previewFile?.projectInfo) {
      navigate(RoutesPaths.project.view(previewFile.projectInfo.id).invoices)
    }
  }

  // Recent Files opens the exact same `FileViewer` instance as All Files, so
  // it gets the exact same wired action set via the same hook — not a
  // second, poorer implementation.
  const actions = useGlobalFileActions({
    fileItems: recent.items,
    reload: recent.reload,
    onViewProject: navigateToProject
  })

  const handleVersionUploaded = (newVersion: ProjectDocument) => {
    const previousFile = actions.uploadingNewVersionFile
    actions.setUploadingNewVersionFile(null)
    if (previousFile) {
      const updated = mapDocumentToFileItem(newVersion, recent.rights)
      favorites.migrateFavorite(previousFile.key, fileItemKey("document", newVersion.id))
      if (previewFile?.key === previousFile.key) setPreviewFile(updated)
    }
    void recent.reload()
  }

  const handleVersionRestored = async (version: ProjectDocument) => {
    const previousFile = actions.versionHistoryFile
    const restored = await actions.restoreVersion(version.id)
    if (restored && previousFile) {
      const updated = mapDocumentToFileItem(restored, recent.rights)
      favorites.migrateFavorite(previousFile.key, fileItemKey("document", restored.id))
      if (previewFile?.key === previousFile.key) setPreviewFile(updated)
    }
    return !!restored
  }

  const handlePreviewVersion = (document: ProjectDocument) => {
    setPreviewFile(mapDocumentToFileItem(document, recent.rights))
  }

  if (overviewState.status === "error") {
    return (
      <Empty description={t("globalFiles.overview.loadFailed", "Couldn't load Files overview.")}>
        <Button onClick={overviewState.refresh}>{t("common.retry", "Retry")}</Button>
      </Empty>
    )
  }

  const overview = overviewState.status === "loaded" ? overviewState.overview : null
  const loadingKpis = overviewState.status === "loading"

  const kpiTiles: KpiTile[] = [
    {
      id: "global_files_total",
      title: t("globalFiles.kpi.allFiles", "All Files"),
      value: overview?.files.total ?? 0,
      drill_to: RoutesPaths.files.list({ tab: "all-files" }),
      icon: "document",
      compare: overview
        ? {
            value: overview.files.total,
            delta: overview.files.added_this_month,
            delta_pct: null
          }
        : null,
      compareLabel: t("globalFiles.kpi.thisMonth", "this month"),
      description: t(
        "globalFiles.kpi.allFilesDescription",
        "Every document and photo you have access to, company-wide."
      )
    },
    ...(overview?.proposals
      ? [
          {
            id: "global_files_proposals",
            title: t("globalFiles.kpi.proposals", "Proposals"),
            value: overview.proposals.total,
            drill_to: RoutesPaths.files.list({
              tab: "business-documents",
              filter: "proposals"
            }),
            icon: "check",
            compare: {
              value: overview.proposals.total,
              delta: overview.proposals.pending,
              delta_pct: null
            },
            compareLabel: t("globalFiles.kpi.pending", "pending"),
            description: t(
              "globalFiles.kpi.proposalsDescription",
              "Business proposals — the record itself, not the generated PDF."
            )
          } satisfies KpiTile
        ]
      : []),
    ...(overview?.contracts
      ? [
          {
            id: "global_files_contracts",
            title: t("globalFiles.kpi.contracts", "Contracts"),
            value: overview.contracts.total,
            drill_to: RoutesPaths.files.list({
              tab: "business-documents",
              filter: "contracts"
            }),
            icon: "briefcase",
            compare: {
              value: overview.contracts.total,
              delta: overview.contracts.need_action,
              delta_pct: null
            },
            compareLabel: t("globalFiles.kpi.needAction", "need action"),
            description: t(
              "globalFiles.kpi.contractsDescription",
              "Business contracts — the record itself, not the generated PDF."
            )
          } satisfies KpiTile
        ]
      : []),
    {
      id: "global_files_needs_attention",
      title: t("globalFiles.kpi.needsAttention", "Need Attention"),
      value: overview?.needs_attention.total ?? 0,
      drill_to: RoutesPaths.files.list({ tab: "all-files", filter: "needs_attention" }),
      icon: "alert",
      description: t(
        "globalFiles.kpi.needsAttentionDescription",
        "Overdue reviews, contracts awaiting signature, changes requested and untyped documents."
      )
    }
  ]

  const totalBreakdown = overview?.document_breakdown.reduce((sum, row) => sum + row.count, 0) ?? 0

  return (
    <Flex vertical gap="large">
      <Row gutter={[16, 16]}>
        {kpiTiles.map((tile) => (
          <Col xs={24} sm={12} lg={6} key={tile.id}>
            <KpiCard tile={tile} loading={loadingKpis} />
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={t("globalFiles.breakdown.title", "Document Breakdown")}
            size="small"
          >
            {loadingKpis ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : !overview?.document_breakdown.length ? (
              <Empty description={t("globalFiles.breakdown.empty", "No files yet")} />
            ) : (
              <Flex vertical gap={10}>
                {overview.document_breakdown.map((row) => (
                  <Flex key={row.document_type ?? "uncategorized"} vertical gap={2}>
                    <Flex justify="space-between">
                      <Text>{row.label}</Text>
                      <Text type="secondary">{row.count}</Text>
                    </Flex>
                    <div className="h-1.5 w-full rounded-full bg-fill-tertiary overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{
                          width: `${totalBreakdown ? (row.count / totalBreakdown) * 100 : 0}%`
                        }}
                      />
                    </div>
                  </Flex>
                ))}
              </Flex>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={t("globalFiles.health.title", "Document Health")}
            size="small"
            extra={
              <Button
                type="link"
                size="small"
                onClick={() => onNavigateToTab("all-files", { filter: "needs_attention" })}
              >
                {t("globalFiles.health.viewAll", "View all →")}
              </Button>
            }
          >
            {loadingKpis ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : !overview?.needs_attention.breakdown.some((row) => row.count > 0) ? (
              <Empty
                description={t("globalFiles.health.empty", "Nothing needs attention right now")}
              />
            ) : (
              <Flex vertical gap={8}>
                {overview.needs_attention.breakdown
                  .filter((row) => row.count > 0)
                  .map((row) => (
                    <Flex
                      key={row.key}
                      justify="space-between"
                      align="center"
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer rounded px-1 py-0.5 hover:bg-fill-tertiary"
                      onClick={() => onNavigateToTab("all-files", { filter: row.key })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          onNavigateToTab("all-files", { filter: row.key })
                        }
                      }}
                    >
                      <Text>{row.label}</Text>
                      <Flex align="center" gap={6}>
                        <Text strong>{row.count}</Text>
                        <Text type="secondary">→</Text>
                      </Flex>
                    </Flex>
                  ))}
              </Flex>
            )}
          </Card>
        </Col>
      </Row>

      <Card
        title={t("globalFiles.recent.title", "Recent Files")}
        size="small"
        extra={
          <Button type="link" size="small" onClick={() => onNavigateToTab("all-files")}>
            {t("globalFiles.recent.viewAll", "View all →")}
          </Button>
        }
      >
        {recent.loading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : !recentFiles.length ? (
          <Empty
            description={t(
              "globalFiles.recent.empty",
              "Files uploaded to your accessible projects and leads will appear here."
            )}
          />
        ) : (
          <Flex vertical>
            {recentFiles.map((file) => (
              <Flex
                key={file.key}
                align="center"
                gap="middle"
                className="cursor-pointer rounded px-2 py-2 hover:bg-fill-tertiary"
                onClick={() => setPreviewFile(file)}
              >
                <span className="text-lg [&_svg]:text-lg">{getFileIcon(file.mimeType)}</span>
                <Flex vertical gap={0} className="min-w-0 flex-1">
                  <Text ellipsis>{file.name}</Text>
                  {file.projectInfo ? (
                    <Text type="secondary" className="text-xs">
                      {(file.projectInfo.is_project
                        ? t("globalFiles.relatedTo.project", "Project")
                        : t("globalFiles.relatedTo.lead", "Lead")) +
                        ` · ${file.projectInfo.uid}` +
                        (file.projectInfo.name ? ` · ${file.projectInfo.name}` : "")}
                    </Text>
                  ) : (
                    file.isCompanyScoped && (
                      <Text type="secondary" className="text-xs">
                        {t("globalFiles.location.companyFiles", "Company Files")}
                      </Text>
                    )
                  )}
                </Flex>
                <Text type="secondary" className="text-xs whitespace-nowrap">
                  {t("globalFiles.recent.updated", "Updated {{date}}", {
                    date: getFormatDate(file.updated, "MMM D, h:mm A") ?? ""
                  })}
                </Text>
              </Flex>
            ))}
          </Flex>
        )}
      </Card>

      {previewFile && (
        <FileViewer
          file={previewFile}
          items={recentFiles}
          onClose={() => setPreviewFile(null)}
          fileActions={actions.fileActions}
          isFavorite={(f) => favorites.isFavorite(f.key)}
          onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
          onViewProject={navigateToProject}
          onRename={(f) => actions.setRenamingFile(f)}
          documentTypeOptions={actions.documentTypeOptions}
          documentTypesFailed={actions.documentTypesFailed}
          onChangeDocumentType={actions.updateDocumentType}
          onLinkRecord={(f) => actions.setLinkingFile(f)}
          onViewRecord={handleViewRecord}
          onUploadNewVersion={(f) => actions.setUploadingNewVersionFile(f)}
          onViewVersionHistory={(f) => actions.setVersionHistoryFile(f)}
          statusOptions={actions.statusOptions}
          statusesFailed={actions.statusesFailed}
          onChangeStatus={actions.updateStatus}
          onRequestReview={(f) => actions.setRequestReviewFile(f)}
          onViewComments={(f) => actions.setCommentsFile(f)}
          onViewActivity={(f) => actions.setActivityFile(f)}
          onShare={(f) => actions.setSharingFile(f)}
          onToggleCustomerVisibility={actions.updateCustomerVisibility}
        />
      )}

      {actions.sharingFile && (
        <ShareDialog file={actions.sharingFile} onClose={() => actions.setSharingFile(null)} />
      )}

      {actions.linkingFile && (
        <GlobalLinkRecordModal
          currentRecord={actions.linkingFile.relatedRecord ?? null}
          onClose={() => actions.setLinkingFile(null)}
          onSubmit={actions.linkRecord}
          onUnlink={actions.unlinkRecord}
        />
      )}

      {actions.uploadingNewVersionFile && (
        <UploadNewVersionModal
          projectId={actions.uploadingNewVersionFile.projectInfo?.id}
          file={actions.uploadingNewVersionFile}
          onClose={() => actions.setUploadingNewVersionFile(null)}
          onUploaded={handleVersionUploaded}
        />
      )}

      {actions.versionHistoryFile && (
        <VersionHistoryDrawer
          open
          fileName={actions.versionHistoryFile.name}
          documentId={actions.versionHistoryFile.id}
          onClose={() => actions.setVersionHistoryFile(null)}
          onPreviewVersion={handlePreviewVersion}
          onRestore={handleVersionRestored}
        />
      )}

      {actions.renamingFile && (
        <RenameFileModal
          fileName={actions.renamingFile.name}
          onClose={() => actions.setRenamingFile(null)}
          onSubmit={(next) => actions.renameFile(actions.renamingFile as FileItem, next)}
        />
      )}

      {actions.assigningDocumentTypeFile && (
        <AssignDocumentTypeModal
          file={actions.assigningDocumentTypeFile}
          options={actions.documentTypeOptions}
          loadFailed={actions.documentTypesFailed}
          onClose={() => actions.setAssigningDocumentTypeFile(null)}
          onSubmit={(value) =>
            actions.updateDocumentType(actions.assigningDocumentTypeFile as FileItem, value)
          }
        />
      )}

      {actions.requestReviewFile && (
        <RequestReviewModal
          file={actions.requestReviewFile}
          onClose={() => actions.setRequestReviewFile(null)}
          onSubmit={actions.submitRequestReview}
        />
      )}

      {actions.commentsFile && (
        <CommentsDrawer file={actions.commentsFile} onClose={() => actions.setCommentsFile(null)} />
      )}

      {actions.activityFile && (
        <ActivityDrawer file={actions.activityFile} onClose={() => actions.setActivityFile(null)} />
      )}
    </Flex>
  )
}
